import type Konva from "konva";
import { useEffect, useState } from "react";
import { Circle, Group, Image as KonvaImage, Rect, Text } from "react-konva";
import { solidOf } from "../../lib/position-tone.js";
import type { PlayerRow } from "../../repos/players.js";
import { CARD_PALETTE, type Widget } from "./snapshot.js";

/** Tiny replacement for the `use-image` package — loads an HTMLImageElement. */
function useImage(url: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) {
      setImg(null);
      return;
    }
    const el = new window.Image();
    el.crossOrigin = "anonymous";
    let active = true;
    el.onload = () => {
      if (active) setImg(el);
    };
    el.onerror = () => {
      if (active) setImg(null);
    };
    el.src = url;
    return () => {
      active = false;
    };
  }, [url]);
  return img;
}

export interface WidgetsLayerProps {
  widgets: readonly Widget[];
  selectedIds: ReadonlySet<string>;
  /** Set to true when the select tool is active. Disables drag otherwise. */
  draggable: boolean;
  /** Look-up table for fresh player data (image, current name) by id. */
  playerById?: ReadonlyMap<string, PlayerRow>;
  onSelect: (id: string) => void;
  onDragStart?: (id: string, x: number, y: number) => void;
  onDragMove: (id: string, x: number, y: number) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  /** Double-tap / double-click → open the widget editor (rename / re-pick). */
  onEdit: (id: string) => void;
}

/**
 * Whiteboard entity-widget layer.
 *
 * Widgets are richer than stickies — they reference real domain entities
 * (e.g. a player row) or carry structured copy (drill cards with a title +
 * body). The layer dispatches on `kind` and lets each variant render its
 * own Konva sub-tree inside a shared draggable `Group`.
 */
export function WidgetsLayer({
  widgets,
  selectedIds,
  draggable,
  playerById,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onEdit,
}: WidgetsLayerProps) {
  return (
    <>
      {widgets.map((w) => {
        const selected = selectedIds.has(w.id);
        return (
          <Group
            key={w.id}
            id={`wb-widget-${w.id}`}
            x={w.x}
            y={w.y}
            draggable={draggable}
            onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
              e.cancelBubble = true;
              if (!selected) onSelect(w.id);
            }}
            onTouchStart={(e: Konva.KonvaEventObject<TouchEvent>) => {
              e.cancelBubble = true;
              if (!selected) onSelect(w.id);
            }}
            onDblClick={() => onEdit(w.id)}
            onDblTap={() => onEdit(w.id)}
            onDragStart={(e) => onDragStart?.(w.id, e.target.x(), e.target.y())}
            onDragMove={(e) => onDragMove(w.id, e.target.x(), e.target.y())}
            onDragEnd={(e) => onDragEnd(w.id, e.target.x(), e.target.y())}
          >
            {w.kind === "player" ? (
              <PlayerWidgetView
                widget={w}
                selected={selected}
                imageRef={playerById?.get(w.playerId)?.image_ref ?? null}
              />
            ) : (
              <CardWidgetView widget={w} selected={selected} />
            )}
          </Group>
        );
      })}
    </>
  );
}

function PlayerWidgetView({
  widget,
  selected,
  imageRef,
}: {
  widget: Extract<Widget, { kind: "player" }>;
  selected: boolean;
  imageRef: string | null;
}) {
  const tone = solidOf(widget.position);
  const radius = Math.min(widget.h, widget.w) * 0.42;
  const cx = radius + 12;
  const cy = widget.h / 2;
  const img = useImage(imageRef);
  return (
    <>
      <Rect
        x={6}
        y={8}
        width={widget.w}
        height={widget.h}
        fill="rgba(0,0,0,0.35)"
        cornerRadius={14}
        listening={false}
      />
      <Rect
        x={0}
        y={0}
        width={widget.w}
        height={widget.h}
        fill="#0f1620"
        stroke={selected ? "#97fc00" : "rgba(255,255,255,0.18)"}
        strokeWidth={selected ? 3 : 1.5}
        cornerRadius={14}
      />
      <Circle
        x={cx}
        y={cy}
        radius={radius}
        fill={tone.fill}
        stroke={tone.stroke}
        strokeWidth={3}
        listening={false}
      />
      {img ? (
        <KonvaImage
          x={cx - radius + 3}
          y={cy - radius + 3}
          width={(radius - 3) * 2}
          height={(radius - 3) * 2}
          image={img}
          cornerRadius={radius - 3}
          listening={false}
        />
      ) : (
        <Text
          x={cx - radius}
          y={cy - radius}
          width={radius * 2}
          height={radius * 2}
          text={widget.shirt != null ? String(widget.shirt) : initials(widget.name)}
          fontSize={radius * 0.95}
          fontStyle="800"
          fill={tone.fg}
          align="center"
          verticalAlign="middle"
          listening={false}
        />
      )}
      <Text
        x={cx + radius + 14}
        y={18}
        width={widget.w - (cx + radius + 14) - 12}
        text={widget.name || "Unbenannt"}
        fontSize={22}
        fontStyle="700"
        fill="#f5f8ff"
        ellipsis
        wrap="none"
        listening={false}
      />
      <Text
        x={cx + radius + 14}
        y={46}
        width={widget.w - (cx + radius + 14) - 12}
        text={widget.position ? widget.position : "—"}
        fontSize={16}
        fontStyle="600"
        fill="rgba(245,248,255,0.65)"
        listening={false}
      />
      {widget.shirt != null && (
        <Text
          x={cx + radius + 14}
          y={widget.h - 30}
          text={`#${widget.shirt}`}
          fontSize={14}
          fontStyle="600"
          fill="rgba(245,248,255,0.5)"
          listening={false}
        />
      )}
    </>
  );
}

function CardWidgetView({
  widget,
  selected,
}: {
  widget: Extract<Widget, { kind: "card" }>;
  selected: boolean;
}) {
  const palette = CARD_PALETTE[widget.accent];
  return (
    <>
      <Rect
        x={6}
        y={8}
        width={widget.w}
        height={widget.h}
        fill="rgba(0,0,0,0.35)"
        cornerRadius={14}
        listening={false}
      />
      <Rect
        x={0}
        y={0}
        width={widget.w}
        height={widget.h}
        fill={palette.paper}
        stroke={selected ? "#97fc00" : undefined}
        strokeWidth={selected ? 3 : 0}
        cornerRadius={14}
      />
      <Rect
        x={-2}
        y={0}
        width={9}
        height={widget.h}
        fill={palette.stroke}
        cornerRadius={[14, 0, 0, 14]}
        listening={false}
      />
      <Text
        x={18}
        y={14}
        width={widget.w - 32}
        text={widget.title || "Übungsblock"}
        fontSize={22}
        fontStyle="800"
        fill={palette.ink}
        wrap="word"
        listening={false}
      />
      <Text
        x={18}
        y={48}
        width={widget.w - 32}
        height={widget.h - 60}
        text={widget.body || "Doppelklicken zum Bearbeiten"}
        fontSize={16}
        fontStyle="500"
        fill={widget.body ? palette.subtle : "rgba(255,255,255,0.35)"}
        wrap="word"
        listening={false}
      />
    </>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}
